import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Volume2, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

type Operation = "+" | "-";

const arabicNumber = (n: number) => {
  const map = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return String(Math.abs(n)).split("").map((d) => map[parseInt(d)]).join("");
};

const generateQuestion = () => {
  const op: Operation = Math.random() > 0.5 ? "+" : "-";
  let a: number, b: number, answer: number;

  if (op === "+") {
    a = Math.floor(Math.random() * 10) + 1;
    b = Math.floor(Math.random() * 10) + 1;
    answer = a + b;
  } else {
    a = Math.floor(Math.random() * 10) + 3;
    b = Math.floor(Math.random() * Math.min(a, 10)) + 1;
    answer = a - b;
  }

  const wrongSet = new Set<number>();
  while (wrongSet.size < 3) {
    const offset = Math.floor(Math.random() * 5) + 1;
    const wrong = Math.random() > 0.5 ? answer + offset : answer - offset;
    if (wrong !== answer && wrong >= 0) wrongSet.add(wrong);
  }

  const options = [...wrongSet, answer].sort(() => Math.random() - 0.5);
  return { a, b, op, answer, options };
};

const OP_EMOJI: Record<string, string> = { "+": "➕", "-": "➖" };

const REWARD_EMOJIS = ["🌟", "⭐", "🎯", "🏅", "💎", "🎪", "🚀", "🎠"];

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function MathGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [data, setData] = useState(generateQuestion);
  const [selected, setSelected] = useState<number | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [completed, setCompleted] = useState(false);

  const { speak, isSpeaking, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (selected !== null || round >= totalRounds) return;
      // Extract number from speech (Arabic or English)
      const arabicToNum: Record<string, string> = { "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9" };
      const normalized = transcript.replace(/[٠-٩]/g, d => arabicToNum[d] || d);
      const nums = normalized.match(/\d+/g);
      if (nums) {
        const spoken = parseInt(nums[0]);
        if (data.options.includes(spoken)) handleSelect(spoken);
      }
    },
  });

  const handleSelect = (n: number) => {
    if (selected !== null) return;
    setSelected(n);
    const correct = n === data.answer;
    if (correct) {
      const bonus = streak >= 3 ? 15 : 10;
      setScore((s) => s + bonus);
      setStreak((s) => s + 1);
      if (streak >= 2) setShowCelebration(true);
      encourageCorrect();
    } else {
      setStreak(0);
      encourageWrong();
    }
    setTimeout(() => {
      setSelected(null);
      setShowCelebration(false);
      setRound((r) => r + 1);
      setData(generateQuestion());
    }, timeout);
  };

  const reset = () => {
    setScore(0);
    setStreak(0);
    setRound(0);
    setData(generateQuestion());
    setSelected(null);
    setCompleted(false);
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 15;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ rotate: [0, 360] }} transition={{ repeat: Infinity, duration: 3, ease: "linear" }} className="text-6xl">🧮</motion.div>
            <h2 className="text-2xl font-bold text-foreground">عبقري! 🎉</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 15)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Badge className="bg-gradient-to-r from-orange-400 to-red-500 text-white border-0 text-sm px-3 py-1">🔥 سلسلة {streak}</Badge>
          </motion.div>
        )}
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-yellow-400 to-orange-500" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round}
          initial={{ opacity: 0, rotateX: 90 }}
          animate={{ opacity: 1, rotateX: 0 }}
          exit={{ opacity: 0, rotateX: -90 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-8 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full"
                    onClick={() => speak(`كم يساوي ${data.a} ${data.op === "+" ? "زائد" : "ناقص"} ${data.b}؟`)}
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={isListening ? "destructive" : "ghost"}
                    size="icon"
                    className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`}
                    onClick={() => isListening ? stopListening() : startListening()}
                    disabled={selected !== null}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-sm font-medium opacity-80 mb-3">احسب الناتج</p>
                <div className="flex items-center justify-center gap-4 text-5xl font-bold">
                  <motion.span
                    initial={{ x: -30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    {arabicNumber(data.a)}
                  </motion.span>
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="text-3xl"
                  >
                    {OP_EMOJI[data.op]}
                  </motion.span>
                  <motion.span
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {arabicNumber(data.b)}
                  </motion.span>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-3xl"
                  >
                    =
                  </motion.span>
                  <motion.span
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="text-3xl"
                  >
                    ❓
                  </motion.span>
                </div>
                <p className="text-lg opacity-70 mt-3">{data.a} {data.op} {data.b} = ؟</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt, i) => {
          const isSelected = selected === opt;
          const showResult = selected !== null;
          const isAnswer = opt === data.answer;
          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-5 text-center transition-all ${bgClass}`}
            >
              <span className="text-3xl font-bold text-foreground block">{arabicNumber(opt)}</span>
              <span className="text-sm text-muted-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Celebration */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            {[...Array(10)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute text-3xl"
                initial={{ opacity: 1, x: 0, y: 0 }}
                animate={{ opacity: 0, x: (Math.random() - 0.5) * 400, y: (Math.random() - 0.5) * 400 }}
                transition={{ duration: 1 }}
              >
                {REWARD_EMOJIS[i % REWARD_EMOJIS.length]}
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
